import React, { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useAxios from "@libs/axios/useAxios";

const FIELDS = ["origin", "fabric_en", "fabric_ko", "wash_info_en", "wash_info_ko", "origin_ja", "fabric_ja", "wash_info_ja"];
const TARGETS = ["origin_ja", "fabric_ja", "wash_info_ja"];

// Blank translations are filled automatically. Existing/manual Japanese is never overwritten.
const InformationTranslation = () => {
  const { control, getValues, setValue, register } = useFormContext();
  const values = useWatch({ control, name: FIELDS });
  const signature = JSON.stringify(values);
  const { axiosInstance } = useAxios();
  const client = useRef(axiosInstance);
  client.current = axiosInstance;
  const generated = useRef<Record<string, string>>({});
  const previousSources = useRef("");
  const [retry, setRetry] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const snapshot = Object.fromEntries(FIELDS.map((name) => [name, getValues(name) || ""]));
    const sourceKey = JSON.stringify(FIELDS.slice(0, 5).map((name) => snapshot[name]));
    const sourceChanged = previousSources.current !== "" && sourceKey !== previousSources.current;
    const payload = { ...snapshot };
    if (sourceChanged) {
      TARGETS.forEach((key) => {
        if (generated.current[key] && snapshot[key] === generated.current[key]) payload[key] = "";
      });
    }
    const sourceFor: Record<string, string> = { origin_ja: payload.origin, fabric_ja: payload.fabric_en || payload.fabric_ko, wash_info_ja: payload.wash_info_en || payload.wash_info_ko };
    const pending = TARGETS.filter((key) => sourceFor[key] && !payload[key]);
    if (!pending.length) { setValue("_translation_status", "idle"); return; }
    setStatus("pending");
    setValue("_translation_status", "pending");
    setError("");
    const timer = setTimeout(async () => {
      setStatus("loading");
      setValue("_translation_status", "loading");
      try {
        const response = await client.current.post("/api/v1/product-information/translate-ja", payload, { timeout: 40000 });
        if (cancelled) return;
        const translated = response.data.data;
        pending.forEach((key) => {
          // Protect edits made while the request was in flight.
          if ((getValues(key) || "") === snapshot[key] && translated?.[key]) {
            generated.current[key] = translated[key];
            setValue(key, translated[key], { shouldDirty: true });
          }
        });
        previousSources.current = sourceKey;
        setStatus("done");
        setValue("_translation_status", "done");
      } catch {
        if (cancelled) return;
        setStatus("failed");
        setValue("_translation_status", "failed");
        setError("일본어 자동번역을 완료하지 못했습니다. 일본어를 직접 입력하거나 다시 시도해주세요.");
      }
    }, 800);
    return () => { cancelled = true; clearTimeout(timer); };
  // The serialized values keep the request independent of render-time client identities.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, retry, getValues, setValue]);

  return (
    <div style={{ fontSize: "1.3rem", display: "flex", flexDirection: "column", gap: "0.8rem" }}>
      <label>원산지 (일문)<input aria-label="원산지 (일문)" maxLength={255} {...register("origin_ja")} style={{ marginLeft: "1rem", padding: "0.8rem", border: "1px solid #ccc" }} /></label>
      <p aria-live="polite">{status === "pending" || status === "loading" ? "일본어 상품 정보를 번역하고 있습니다…" : status === "done" ? "일본어 번역을 입력했습니다. 필요하면 수정할 수 있습니다." : "국문·영문 상품 정보의 빈 일본어 항목을 자동으로 번역합니다. 직접 입력한 일본어는 유지됩니다."}</p>
      {error && <div role="alert">{error} <button type="button" onClick={() => setRetry((value) => value + 1)}>번역 다시 시도</button></div>}
    </div>
  );
};
export default InformationTranslation;
